import { createAction, createSlice } from "@reduxjs/toolkit";

import listFactory from "../../../store/listFactory";

import type { NotificationTestResponse } from "../../../models/NotificationTestResponse";
import type { RootState } from "../../../store";
import type { UUID } from "../../../utils";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { CashManagementNotificationSettings } from "../models/CashManagement";

const {
  slicePart,
  selectors,
} = listFactory<CashManagementNotificationSettings>(
  "cashManagementNotification"
);

const initialState = {
  ...slicePart.initialState,
  testResponse: {} as NotificationTestResponse,
  fetchedEnvironment: null,
};

export const slice = createSlice({
  ...slicePart,
  initialState,
  reducers: {
    setItem: slicePart.reducers.setItem,
    updateItem: slicePart.reducers.updateItem,
    updateNotificationStatus: (state, action) => {
      const { id, data } = action.payload;

      // only `enable` because `disable` is done on backend after some time
      if (data.status) {
        slicePart.reducers.updateItem(state, {
          ...action,
          payload: {
            id,
            itemPart: {
              enabled: true,
            },
          },
        });
      }
    },
    setNotificationTestResponse: (state, action) => {
      const { data } = action.payload;

      state.testResponse = data;
    },
    setFetchEnvironment: (state, action) => {
      const { environment } = action.payload;

      state.fetchedEnvironment = environment;
    },
  },
});

export const fetchCashManagementNotificationsSettings = createAction<{
  id: UUID;
}>("fetchCashManagementNotificationsSettings");

export const updateCashManagementNotificationsSettings = createAction<{
  id: UUID;
  data: CashManagementNotificationSettings;
}>("updateCashManagementNotificationsSettings");

export const createCashManagementNotificationsSettings = createAction<{
  id: UUID;
  data: CashManagementNotificationSettings;
}>("createCashManagementNotificationsSettings");

export const testCashManagementNotifications = createAction<ObjectWithId>(
  "testCashManagementNotifications"
);

export const setCashManagementNotificationsSettingsActiveFlag = createAction<{
  id: UUID;
  data: CashManagementNotificationSettings;
}>("setCashManagementNotificationsSettingsActiveFlag");

export const {
  updateNotificationStatus,
  setNotificationTestResponse,
  setItem: setNotificationsSettings,
  updateItem,
  setFetchEnvironment,
} = slice.actions;

export const { selectItem: selectNotificationsSettings } = selectors;

export const selectNotificationsTestResponse = (state: RootState) => {
  return state.cashManagementNotification.testResponse;
};

export const selectFetchedEnvironment = (state: RootState) =>
  state.cashManagementNotification.fetchedEnvironment;

export default slice.reducer;

export const notificationsOperationPaths = (id?: UUID) => {
  return {
    details: ["cashManagement", "settlementNotifications", id, "details"],
    update: ["cashManagement", "settlementNotifications", "update", id],
    create: ["cashManagement", "settlementNotifications", "create", id],
    active: ["cashManagement", "settlementNotifications", id, "activeFlag"],
  };
};
